import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, CalendarClock, LogOut } from "lucide-react";
import { TOOTH_TYPES, ARCH } from "@/data/teeth";

const SIZE = { central: [26, 34], lateral: [22, 30], canine: [22, 32], molar1: [32, 30], molar2: [34, 30] };
const COLORS = ["#38bdf8", "#f5b82e", "#ff6b6b", "#0d9488", "#a78bfa"];

const pos = (i, jaw) => {
  const deg = 165 - (150 * i) / (ARCH.length - 1);
  const a = (deg * Math.PI) / 180;
  const x = 180 + 128 * Math.cos(a);
  const y = jaw === "up" ? 160 - 118 * Math.sin(a) : 172 + 118 * Math.sin(a);
  return { x, y, rot: jaw === "up" ? 90 - deg : deg - 90 };
};

const Tooth = ({ type, i, jaw, active, onPick }) => {
  const { x, y, rot } = pos(i, jaw);
  const [w, h] = SIZE[type];
  const on = active?.i === i && active?.jaw === jaw;
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`} onClick={() => onPick({ type, i, jaw })} role="button" tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onPick({ type, i, jaw })} data-testid={`hero-tooth-${jaw}-${i}`}
      className="cursor-pointer outline-none" aria-label={TOOTH_TYPES[type].name}>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={type.startsWith("molar") ? 10 : 12}
        fill={on ? COLORS[i % COLORS.length] : "#ffffff"} stroke="#1e293b" strokeWidth="3"
        className="transition-all duration-300 hover:fill-[#ccfbf1]" style={{ transform: on ? "scale(1.18)" : "scale(1)", transformBox: "fill-box", transformOrigin: "center" }} />
      {on && <circle r="3" cy={-4} fill="#fff" />}
    </g>
  );
};

const Mouth = ({ active, onPick }) => (
  <svg viewBox="0 0 360 330" className="w-full" data-testid="hero-mouth-chart">
    <ellipse cx="180" cy="165" rx="172" ry="158" fill="#ffe4e6" stroke="#1e293b" strokeWidth="3" />
    <ellipse cx="180" cy="168" rx="98" ry="60" fill="#fda4af" opacity=".55" />
    <text x="180" y="162" textAnchor="middle" className="font-display" fontSize="15" fontWeight="600" fill="#9f1239">Tap a tooth!</text>
    <text x="180" y="182" textAnchor="middle" fontSize="11" fill="#9f1239">20 baby teeth</text>
    {ARCH.map((t, i) => <Tooth key={`u${i}`} type={t} i={i} jaw="up" active={active} onPick={onPick} />)}
    {ARCH.map((t, i) => <Tooth key={`l${i}`} type={t} i={i} jaw="low" active={active} onPick={onPick} />)}
  </svg>
);

const InfoCard = ({ active, mode }) => {
  const t = TOOTH_TYPES[active.type];
  const [inAge, outAge] = t[active.jaw];
  return (
    <motion.div key={`${active.jaw}${active.i}${mode}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="rounded-3xl border-2 border-slate-800 bg-white p-5 shadow-[6px_6px_0_#1e293b]" data-testid="hero-tooth-info">
      <p className="text-xs font-bold uppercase tracking-widest text-teal-600">{active.jaw === "up" ? "Upper" : "Lower"} {t.name}</p>
      <p className="font-display text-2xl font-bold text-slate-800">{mode === "kids" ? `Meet the ${t.kidName}!` : t.name}</p>
      <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
        <span className="inline-flex items-center gap-1 rounded-full bg-[#ebfbfa] px-3 py-1 text-teal-800"><CalendarClock className="h-3.5 w-3.5" />Comes in: {inAge}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-[#fef7e5] px-3 py-1 text-amber-800"><LogOut className="h-3.5 w-3.5" />Falls out: {outAge}</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">{mode === "kids" ? t.kid : t.parent}</p>
    </motion.div>
  );
};

export const ToothExplorer = ({ mode }) => {
  const [active, setActive] = useState(null);
  return (
    <div className="flex flex-col gap-5">
      <div className="mx-auto w-full max-w-[440px]"><Mouth active={active} onPick={setActive} /></div>
      <AnimatePresence mode="wait">
        {active ? <InfoCard active={active} mode={mode} /> : (
          <motion.div key="hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="rounded-3xl border-2 border-dashed border-teal-300 bg-white/70 p-5 text-sm font-semibold text-slate-600" data-testid="hero-tooth-hint">
            <Sparkles className="mb-2 h-6 w-6 text-[#f5b82e]" />
            {mode === "kids" ? "Tap any tooth to meet it and find out when it wiggles out!" : "Tap a tooth to see when it usually erupts, when it sheds, and one care tip for that tooth."}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
